import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { AgentFrameApiProvider, useAgentFrameApi } from '../Services/AgentFrameApiProvider';
import { AssistantFrameApi } from '../Services/AgentFrameApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';

export function AgentFramesContent({ canEdit }: { canEdit: boolean }) {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getAgentFrames, getAgentFrameById, deleteAgentFrame, createAgentFrame } =
    useAgentFrameApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AssistantFrameApi[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [selectedRows, setSelectedRows] = useState<AssistantFrameApi[]>([]);
  const selectedId = selectedRows.length === 1 ? selectedRows[0].id : null;

  const columns = [
    { name: 'ID', selector: (row: AssistantFrameApi) => row.id, sortable: false },
    { name: 'Name', selector: (row: AssistantFrameApi) => row.name, sortable: false },
    {
      name: 'Type',
      selector: (row: AssistantFrameApi) =>
        row.frame_type === 'CRITERIA'
          ? 'Criteria Based'
          : row.frame_type === 'THEMATIC'
            ? 'Thematics Based'
            : 'Topic Based',
      sortable: false,
    },
    {
      name: 'Status',
      selector: (row: AssistantFrameApi) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    {
      name: 'Created',
      selector: (row: AssistantFrameApi) => formatDateUTC(row.created_at),
      sortable: false,
    },
    {
      name: 'Updated',
      selector: (row: AssistantFrameApi) => formatDateUTC(row.updated_at),
      sortable: false,
    },
  ];

  const handleDelete = (selectedIds: string[]): void => {
    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        setLoading(true);
        await deleteAgentFrame(selectedIds[0]);
        await fetchItems(0, itemsPerPage);
        showAlert('success', 'Item was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      } finally {
        setLoading(false);
      }
    });
  };

  const handleRowClicked = (row: AssistantFrameApi): void => {
    navigate(`/organizations/${organizationId}/agent-frames/${row.id}`, {
      state: { organizationId },
    });
  };

  const createCopy = async (): Promise<void> => {
    if (!selectedId) return;
    setLoading(true);

    try {
      const frameToCopy = await getAgentFrameById(selectedId);

      if (!frameToCopy) {
        showAlert('fail', 'Frame not found');
        setLoading(false);
        return;
      }

      const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      const baseName = frameToCopy.name;
      const safeBaseName = escapeRegExp(baseName);
      const copyNumbers = items
        .map((a) => {
          const match = a.name.match(new RegExp(`^${safeBaseName} \\(Copy (\\d+)\\)$`));
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((n) => n !== null);
      const nextCopyNumber = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

      const newFrameData = {
        name: `${baseName} (Copy ${nextCopyNumber})`,
        frame_type: frameToCopy.frame_type,
        topic: frameToCopy.topic ?? null,
        criteria: frameToCopy.criteria ?? null,
        thematics: frameToCopy.thematics ?? [],
        instruction: frameToCopy.instruction ?? null,
        example: frameToCopy.example ?? null,
        is_active: frameToCopy.is_active,
      };

      await createAgentFrame(newFrameData);

      await fetchItems(0, itemsPerPage);

      showAlert('success', 'Frame copy was successfully created!');
    } catch (error) {
      console.error('Create copy error:', error);
      showAlert('fail', 'Failed to create a copy of the frame!');
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async (skip: number, limit: number): Promise<void> => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getAgentFrames(skip, limit);
      setItems(response.items);
      setTotalItems(response.totalCount);
    } catch (error) {
      setError(error.message);
      showAlert('fail', 'Failed to fetch agents frames');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(0, itemsPerPage);
  }, []);
  console.log('Items', items);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Agent Frames"
                columns={columns}
                data={items}
                selectableRows
                selectableRowsHighlight
                paginationPerPage={itemsPerPage}
                progressPending={loading}
                paginationTotalRows={totalItems}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={fetchItems}
                getRowUrl={(row) => `/organizations/${organizationId}/agent-frames/${row.id}`}
                //searchFields={['name']}
                typeSearch="default"
                onSelectedRowsChange={({ selectedRows }) => {
                  setSelectedRows(selectedRows);
                }}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/agent-frames/new-agent-frame`)
                  }
                >
                  Create New Agent Frame
                </Button>
              )}

              {selectedId && (
                <Button variant="ghost" size="medium" onClick={createCopy}>
                  Create copy
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function AgentFrames({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <AgentFrameApiProvider>
        <AgentFramesContent canEdit={canEdit} />
      </AgentFrameApiProvider>
    </ConfirmationProvider>
  );
}

export default AgentFrames;
